/**
 * Editor content composable
 * Handles content retrieval, parsing, and synchronization for contenteditable editor
 */

import { ref } from 'vue';
import {
  formatMaterialPlaceholder,
  normalizeColorToHex,
} from '@/components/create/material/material-utils';
import {
  parseSeedeTheme,
  parseSeedeStyle,
  parseSeedeDocument,
  parseSeedeReferenceImage,
} from '@/lib/gen/prompt-parser';

/**
 * @param {Object} options
 * @param {import('vue').Ref<HTMLElement>} options.editorRef - Editor element ref
 * @param {Object} options.chatState - Chat state object
 * @param {Function} options.emit - Vue emit function
 * @param {Function} options.adjustHeight - Adjust height function
 * @returns {Object} Content handlers and state
 */
export function useEditorContent(options = {}) {
  const { editorRef, chatState, emit, adjustHeight, syncAssetsWithEditor } = options;

  // Store last synced value to avoid circular updates
  const lastSyncedValue = ref('');
  const isEmpty = ref(true);

  /**
   * Check if node is inside special element (image, tag, color)
   * @param {Node} node
   * @param {HTMLElement} root
   * @returns {boolean}
   */
  const isInsideSpecialElement = (node, root) => {
    let parent = node.parentNode;
    while (parent && parent !== root) {
      if (parent.nodeType === Node.ELEMENT_NODE) {
        const dataType = parent.getAttribute?.('data-type');
        if (dataType === 'image' || dataType === 'tag' || dataType === 'color') {
          return true;
        }
      }
      parent = parent.parentNode;
    }
    return false;
  };

  /**
   * Check if editor is empty (fast version - avoids full text extraction)
   * @returns {boolean}
   */
  const checkEmpty = () => {
    if (!editorRef?.value) return true;
    const editor = editorRef.value;

    // Quick checks first (cheap DOM queries)
    if (editor.querySelector('[data-type="image"]')) return false;
    if (editor.querySelector('[data-type="tag"]')) return false;
    if (editor.querySelector('[data-type="color"]')) return false;

    // Check for text content (faster than getPlainText)
    const textContent = editor.textContent || '';
    return !textContent.trim();
  };

  /**
   * Get plain text content for v-model sync - optimized with TreeWalker
   * @returns {string}
   */
  const getPlainText = () => {
    if (!editorRef?.value) return '';

    const result = [];
    const root = editorRef.value;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        // Skip special element children (will process separately)
        if (node.nodeType === Node.ELEMENT_NODE) {
          const dataType = node.getAttribute?.('data-type');
          if (dataType === 'image' || dataType === 'tag' || dataType === 'color') {
            return NodeFilter.FILTER_ACCEPT;
          }
          // Block element markers
          if (node.nodeName === 'BR') {
            return NodeFilter.FILTER_ACCEPT;
          }
          if (node.nodeName === 'DIV' || node.nodeName === 'P') {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_SKIP;
        }
        // Text nodes: check if inside special element, skip if so
        if (node.nodeType === Node.TEXT_NODE) {
          if (isInsideSpecialElement(node, root)) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let prevWasBlock = false;
    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        result.push(node.textContent);
        prevWasBlock = false;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const dataType = node.getAttribute?.('data-type');

        if (dataType === 'image') {
          const assetId = node.getAttribute('data-asset-id');
          const asset = chatState?.assets?.find((a) => a.id === assetId);
          if (asset) {
            result.push(formatMaterialPlaceholder(asset));
          } else {
            const url = node.getAttribute('data-url');
            const tag = node.getAttribute('data-tag') || '';
            result.push(formatMaterialPlaceholder({ id: assetId, url, name: '', tag }));
          }
          prevWasBlock = false;
        } else if (dataType === 'tag') {
          const tagId = node.getAttribute('data-tag-id');
          const value = node.getAttribute('data-value') || '';
          result.push(`[[tag:${tagId}:${value}]]`);
          prevWasBlock = false;
        } else if (dataType === 'color') {
          const colorValue = node.getAttribute('data-value') || '';
          result.push(colorValue);
          prevWasBlock = false;
        } else if (node.nodeName === 'BR') {
          result.push('\n');
          prevWasBlock = false;
        } else if ((node.nodeName === 'DIV' || node.nodeName === 'P') && !prevWasBlock) {
          // Add newline at block element start (unless first or already has newline)
          if (result.length > 0 && !result[result.length - 1]?.endsWith('\n')) {
            result.push('\n');
          }
          prevWasBlock = true;
        }
      }
    }

    return result.join('');
  };

  /**
   * Get formatted content for submission
   * @returns {{ text: string, images: Array, tags: Array, colors: Array }}
   */
  const getFormattedContent = () => {
    if (!editorRef?.value) return { text: '', images: [], tags: [], colors: [] };

    const clone = editorRef.value.cloneNode(true);
    const images = [];
    const tags = [];
    const colors = [];

    // Collect image materials and replace with @SeedeMaterial format in text
    clone.querySelectorAll('[data-type="image"]').forEach((img) => {
      const assetId = img.getAttribute('data-asset-id');
      const url = img.getAttribute('data-url');
      const tag = img.getAttribute('data-tag') || '';
      // Get full asset info from chatState.assets
      const asset = chatState?.assets?.find((a) => a.id === assetId);
      const finalAsset = asset || { id: assetId, url, tag };
      images.push(finalAsset);
      // Replace with @SeedeMaterial format
      img.replaceWith(formatMaterialPlaceholder(finalAsset));
    });

    // Collect tags
    clone.querySelectorAll('[data-type="tag"]').forEach((tagEl) => {
      tags.push({
        id: tagEl.getAttribute('data-tag-id'),
        label: tagEl.getAttribute('data-label'),
        value: tagEl.getAttribute('data-value'),
      });
      tagEl.replaceWith(` #${tagEl.getAttribute('data-tag-id')} `);
    });

    // Collect colors
    clone.querySelectorAll('[data-type="color"]').forEach((colorEl) => {
      const colorValue = colorEl.getAttribute('data-value');
      const displayValue = colorEl.getAttribute('data-display-value');
      colors.push({
        id: colorEl.getAttribute('data-color-id'),
        value: colorValue,
        displayValue: displayValue,
      });
      colorEl.replaceWith(` ${displayValue || colorValue} `);
    });

    return {
      text: clone.innerText.trim(),
      images,
      tags,
      colors,
    };
  };

  /**
   * Core sync content to v-model (synchronous, optimized)
   */
  const syncContentCore = () => {
    // Always update isEmpty immediately for instant placeholder feedback
    isEmpty.value = checkEmpty();

    const text = getPlainText();
    // Only emit and do heavy work if content actually changed
    if (text === lastSyncedValue.value) {
      return;
    }
    // Update last synced value to avoid watch triggering unnecessary updates
    lastSyncedValue.value = text;
    emit?.('update:modelValue', text);
    adjustHeight?.();
    // Sync assets state (remove deleted assets)
    syncAssetsWithEditor?.();
  };

  /**
   * Sync content to v-model (synchronous, no debounce for better responsiveness)
   * @param {Object} options
   * @param {boolean} options.immediate - Kept for API compatibility, ignored now
   */
  const syncContent = () => {
    syncContentCore();
  };

  /**
   * Parse text with @SeedeMaterial and HEX color values, return segments array by position
   * @param {string} text
   * @returns {Array} Segment array
   */
  const parseTextWithMaterials = (text) => {
    if (!text) return [];

    const segments = [];
    // Match @SeedeMaterial({...}) or standalone HEX color #RGB/#RRGGBB
    const materialRegex = /@SeedeMaterial\((\{[\s\S]*?\})\)/g;
    const hexColorRegex = /#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})(?=\s|$)/g;

    // Collect all matches and positions
    const matches = [];

    // Collect Material matches
    let match;
    while ((match = materialRegex.exec(text)) !== null) {
      matches.push({
        type: 'material',
        index: match.index,
        length: match[0].length,
        data: match[1],
        raw: match[0],
      });
    }

    // Collect HEX color matches (ensure standalone, preceded by whitespace or start)
    while ((match = hexColorRegex.exec(text)) !== null) {
      const charBefore = match.index > 0 ? text[match.index - 1] : '';
      // Ensure color is preceded by whitespace or is at text start
      if (match.index === 0 || /\s/.test(charBefore)) {
        matches.push({
          type: 'color',
          index: match.index,
          length: match[0].length,
          data: `#${match[1]}`,
          raw: match[0],
        });
      }
    }

    // Sort by position
    matches.sort((a, b) => a.index - b.index);

    // Build segments
    let lastIndex = 0;
    for (const m of matches) {
      // Skip overlapping matches
      if (m.index < lastIndex) continue;

      // Add text before
      if (m.index > lastIndex) {
        const textBefore = text.slice(lastIndex, m.index);
        if (textBefore) {
          segments.push({ type: 'text', content: textBefore });
        }
      }

      if (m.type === 'material') {
        try {
          let jsonStr = m.data;
          let braceCount = 0;
          let endIndex = 0;
          for (let i = 0; i < jsonStr.length; i++) {
            if (jsonStr[i] === '{') braceCount++;
            if (jsonStr[i] === '}') braceCount--;
            if (braceCount === 0) {
              endIndex = i + 1;
              break;
            }
          }
          if (endIndex > 0) {
            jsonStr = jsonStr.slice(0, endIndex);
          }

          const materialData = JSON.parse(jsonStr);
          segments.push({
            type: 'material',
            content: {
              id:
                materialData.id ||
                `material_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              url: materialData.url,
              name: materialData.filename || '',
              width: materialData.width,
              height: materialData.height,
              meta: { w: materialData.width, h: materialData.height },
              tag: materialData.tag || '',
            },
          });
        } catch {
          segments.push({ type: 'text', content: m.raw });
        }
      } else if (m.type === 'color') {
        segments.push({
          type: 'color',
          content: {
            id: `color_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            value: m.data,
            displayValue: normalizeColorToHex(m.data),
          },
        });
      }

      lastIndex = m.index + m.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const remaining = text.slice(lastIndex);
      if (remaining) {
        segments.push({ type: 'text', content: remaining });
      }
    }

    return segments;
  };

  /**
   * Parse and insert text (handles @SeedeMaterial and other special markers) - maintains position relationship
   * @param {string} text
   * @param {Object} options
   * @param {boolean} options.skipSync - Whether to skip final syncContent call
   * @param {Function} options.insertTextAtCursor - Insert text function
   * @param {Function} options.insertImageNode - Insert image node function
   * @param {Function} options.insertColorTagNode - Insert color tag node function
   * @param {Function} options.addAssetToChatState - Add asset to chatState function
   */
  const parseAndInsertText = (text, parseOptions = {}) => {
    const {
      skipSync = false,
      insertTextAtCursor,
      insertImageNode,
      insertColorTagNode,
      addAssetToChatState,
    } = parseOptions;

    text = text || '';
    text = text.trim();

    // 1. Parse and remove other markers (don't need position maintenance)
    // Parse theme
    const themePayload = parseSeedeTheme(text);
    text = themePayload.content;
    if (themePayload.theme && chatState) {
      chatState.theme = themePayload.theme;
    }

    // Parse style
    const stylePayload = parseSeedeStyle(text);
    text = stylePayload.content;
    if (stylePayload.style && chatState) {
      chatState.style = stylePayload.style;
    }

    // Parse document
    const documentPayload = parseSeedeDocument(text);
    text = documentPayload.content;
    if (documentPayload.documents?.length && chatState) {
      chatState.documents = documentPayload.documents.map((doc) => ({
        ...doc,
        parsed: true,
      }));
    }

    // Parse reference image
    const refImagePayload = parseSeedeReferenceImage(text);
    text = refImagePayload.content;
    if (chatState) {
      const image = refImagePayload.images[0];
      if (image) {
        chatState.image = image;
      }
    }

    // 2. Parse @SeedeMaterial and [[color:value]] to segment array, maintain position relationship
    const segments = parseTextWithMaterials(text);

    // 3. Insert segments in order (images and text separated by newlines, don't duplicate if already separated)
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const prevSegment = i > 0 ? segments[i - 1] : null;
      const nextSegment = i < segments.length - 1 ? segments[i + 1] : null;

      if (segment.type === 'text') {
        // Insert plain text
        insertTextAtCursor?.(segment.content);
      } else if (segment.type === 'material') {
        // Insert material node
        const asset = segment.content;
        // Check if previous text segment already ends with newline, add if not
        if (prevSegment && prevSegment.type === 'text') {
          if (!prevSegment.content.endsWith('\n')) {
            insertTextAtCursor?.('\n');
          }
        }

        // insertImageNode returns false if limit reached
        const inserted = insertImageNode?.(asset);

        if (inserted !== false) {
          // Check next segment: if text and already starts with newline, don't add newline
          const needTrailingNewline =
            !nextSegment ||
            nextSegment.type === 'material' ||
            (nextSegment.type === 'text' && !nextSegment.content.startsWith('\n'));

          if (needTrailingNewline) {
            insertTextAtCursor?.('\n');
          }

          addAssetToChatState?.(asset);
        }
      } else if (segment.type === 'color') {
        // Insert color Tag node
        const colorData = segment.content;
        insertColorTagNode?.(colorData);
      }
    }

    // Sync content based on skipSync parameter
    if (!skipSync) {
      syncContent({ immediate: true });
    }
  };

  return {
    isEmpty,
    lastSyncedValue,
    checkEmpty,
    getPlainText,
    getFormattedContent,
    syncContent,
    syncContentCore,
    parseTextWithMaterials,
    parseAndInsertText,
  };
}
